import { useEffect, useRef } from 'react';
import Icon from './Icon';

// Bottom sheet over a dimmed screen (a centred dialog on wide screens): drag handle, title and a
// 44px close button on top, the main action at the bottom within thumb reach. `badge` sits under the title.
// `dismissible={false}`: a question that must be answered (no close button, Escape and the backdrop do nothing).
export default function Sheet({ open, onClose, title, label, badge, dismissible = true, children }) {
  const ref = useRef(null);
  // The dialog's close event also fires when `open` turns false from outside (e.g. the chooser handing
  // over to the meal sheet); only report closes the user made (Escape, backdrop), and only of this
  // dialog, not of one opened inside it (the photo viewer).
  const openRef = useRef(open);
  openRef.current = open;
  const boxRef = useRef(null);

  useEffect(() => {
    const dialog = ref.current;
    // Opening focuses the sheet itself, never its first field: Safari doesn't focus buttons, so it would
    // skip the close button and focus e.g. a date field, popping up the iPhone's date picker or keyboard.
    boxRef.current.setAttribute('autofocus', '');
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog ref={ref} className="modal modal-bottom sm:modal-middle" onClose={(e) => e.target === ref.current && openRef.current && onClose()} aria-label={title || label}
      onCancel={(e) => !dismissible && e.preventDefault()}>
      <div ref={boxRef} tabIndex={-1} className="modal-box pb-safe max-h-[92dvh] rounded-t-[24px] px-5 pt-3 outline-none sm:rounded-[24px]">
        <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-base-300" aria-hidden="true" />
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex flex-col items-start gap-1.5">
            <h2 className="text-xl font-bold">{title}</h2>
            {badge}
          </div>
          {dismissible && (
            <button type="button" className="btn btn-circle size-11 shrink-0 border-0 bg-base-200" onClick={onClose} aria-label="Đóng">
              <Icon name="x" className="size-5" />
            </button>
          )}
        </div>
        {open && children}
        <div className="h-4" />
      </div>
      {/* Tapping outside closes. A plain button rather than daisyUI's <form method="dialog">, so a sheet can
          sit inside a form (a form can't contain another). */}
      {dismissible && (
        <div className="modal-backdrop">
          <button type="button" tabIndex={-1} onClick={onClose}>Đóng</button>
        </div>
      )}
    </dialog>
  );
}
